import React, { useMemo, useState } from 'react';
import { Box, Text } from '@chakra-ui/react';
import { Textarea, Input, PhoneInput, PlacesInput } from 'formComponents';
import {
  GroupEventInfo,
  MasterInfo,
  ServiceGroupsInfo,
  SpecialistCases,
} from 'types';
import { useField } from 'react-final-form';
import { phoneMask } from 'constants/phone';
import { Dialog, Master, Paper } from 'components';
import { parse } from 'date-fns';
import { find, flatMap } from 'lodash-es';
import { getPriceText } from 'utils';
import { ContainerStyled } from './styles';
import { EventInfo } from './EventInfo';

export const ContactsEvent: React.FC<{
  widgetId?: string;
  address: string;
  masters: MasterInfo[];
  serviceGroups: ServiceGroupsInfo;
  specialistCases: SpecialistCases;
  additionalPersonsAllowed: boolean;
  fieldNameLabel: string;
  fieldCommentLabel: string;
  postEventError: {
    code: string;
    message: string;
    available: number;
  } | null;
  resetPostEventError: () => void;
  onBackClick?: () => void;
}> = ({
  widgetId,
  address,
  masters,
  serviceGroups,
  specialistCases,
  additionalPersonsAllowed,
  fieldNameLabel,
  fieldCommentLabel,
  postEventError,
  resetPostEventError,
  onBackClick,
}) => {
  const ref = React.useRef<HTMLInputElement>();
  const { input: phoneInput } = useField('clientInfo.phone');
  const groupEventField = useField('groupEventInfo');
  const [groupEvent, setGroupEvent] = useState<GroupEventInfo>(
    groupEventField.input.value,
  );
  const personsCountField = useField('clientInfo.personsCount');
  const date = useMemo(
    () => parse(groupEvent.time, 'HH:mm', new Date(groupEvent.date)),
    [groupEvent.date, groupEvent.time],
  );
  const master = useMemo(
    () => find(masters, ({ id }) => id === groupEvent.employeeId),
    [groupEvent.employeeId, masters],
  );
  const service = useMemo(
    () =>
      flatMap(serviceGroups).find((favor) => favor.id === groupEvent.favorId),
    [groupEvent.favorId, serviceGroups],
  );
  const placesLimit = Math.min(
    service?.onlineLimit || groupEvent.available,
    groupEvent.available,
  );
  const isNoBooking = !!(
    postEventError &&
    ['NO_AVAILABLE', 'VISIT_NOT_FOUND', 'BOOKING_STOPPED'].includes(
      postEventError.code,
    )
  );

  const handleChangePhone = () => {
    if (ref?.current?.value) {
      phoneInput.onChange(ref.current.value);
    }
  };

  const gotoGroupEvents = () => {
    resetPostEventError();
    onBackClick?.();
  };

  const updateGroupEvent = () => {
    if (
      postEventError &&
      ['NOT_ENOUGH_AVAILABLE'].includes(postEventError.code) &&
      postEventError.available
    ) {
      setGroupEvent({ ...groupEvent, available: postEventError.available });
    }
    resetPostEventError();
  };

  if (!groupEvent) return null;

  return (
    <ContainerStyled>
      <Box w="100%">
        <Master
          widgetId={widgetId}
          masterInfo={master}
          specialistCases={specialistCases}
        />
        <EventInfo
          widgetId={widgetId}
          address={address}
          master={master}
          service={service}
          date={date}
          specialistCases={specialistCases}
        />
        {additionalPersonsAllowed && (
          <>
            <Paper mt={4}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <div>
                  <Text color="black" textStyle="body1" lineHeight="5">
                    Количество мест
                  </Text>
                  {service?.showPrice && personsCountField.input.value > 0 ? (
                    <>
                      <Text as="span" color="grayText" textStyle="body2">
                        Итого:&nbsp;
                      </Text>
                      <Text
                        as="span"
                        mr={4}
                        color="highlight"
                        textStyle="body2"
                      >
                        {getPriceText(service, personsCountField.input.value)}
                      </Text>
                    </>
                  ) : null}
                </div>
                <PlacesInput
                  name="clientInfo.personsCount"
                  available={placesLimit}
                />
              </Box>
            </Paper>
            {personsCountField.input.value >= placesLimit && (
              <Text
                color="asphalt.1"
                textStyle="body2"
                fontSize="0.75rem"
                align="right"
              >
                Достигнуто максимально доступное количество мест
              </Text>
            )}
          </>
        )}
      </Box>
      <Box w="100%">
        <Text color="black" textStyle="h4" mb={-2}>
          Контактные данные
        </Text>
        <Input name="clientInfo.name" placeholder={fieldNameLabel ?? 'Имя'} />
        <PhoneInput
          mask={phoneMask}
          name="clientInfo.phone"
          placeholder="Телефон"
          type="tel"
          ref={ref as React.Ref<HTMLInputElement>}
          onChange={handleChangePhone}
        />
        <Textarea
          name="clientInfo.comment"
          placeholder={fieldCommentLabel ?? 'Комментарий'}
        />
      </Box>
      {postEventError && (
        <Dialog
          isOpen={!!postEventError}
          titleContent={postEventError.message || 'Произошла ошибка'}
          okText={isNoBooking ? undefined : 'Назад к событиям'}
          closeText={isNoBooking ? 'Назад к событиям' : 'Закрыть'}
          onClose={isNoBooking ? gotoGroupEvents : updateGroupEvent}
          onOk={gotoGroupEvents}
        />
      )}
    </ContainerStyled>
  );
};
